import { z } from 'zod';

export const listMediaQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  perPage: z.coerce.number().int().positive().max(100).default(24),
  kind: z.enum(['image', 'video', 'audio', 'document']).optional(),
  q: z.string().trim().min(1).max(255).optional(),
  /** "unused" → media with no post_media references (delete candidates). */
  filter: z.enum(['all', 'unused']).default('all'),
});

export type ListMediaQuery = z.infer<typeof listMediaQuerySchema>;

/**
 * Editable metadata on an existing media row. File content is
 * immutable (content-addressed by SHA-256 — replacing pixels means
 * uploading a new media row), so PATCH only touches the human-typed
 * descriptive fields. Use `null` (not missing) to clear a field.
 * Refinement requires at least one field to be present so the call
 * isn't a no-op disguised as success.
 */
export const updateMediaSchema = z
  .object({
    altText: z.string().trim().max(500).nullable().optional(),
    caption: z.string().trim().max(1000).nullable().optional(),
  })
  .refine(
    (v) => v.altText !== undefined || v.caption !== undefined,
    'Provide at least one field to update',
  );

export type UpdateMediaInput = z.infer<typeof updateMediaSchema>;

/**
 * Bulk delete by id. Per-row the server still enforces the same
 * "refuse if referenced by a post" rule the single-delete endpoint
 * applies — referenced ids land in `rejected`, freed ids land in
 * `fulfilled`. Capped at 100 ids/call to keep the R2 delete burst
 * bounded.
 */
export const bulkDeleteMediaSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(100),
});

export type BulkDeleteMediaInput = z.infer<typeof bulkDeleteMediaSchema>;
